import {FormControl, FormField, Tooltip} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {not} from 'ramda';
import {isFalse} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useGetVatRatesOptions} from '@dms/shared';
import {FALLBACK_HANDLING_UNIT} from '@dms/warehouse/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ReceiveNoteItemFormType} from './types/ReceiveNoteItemDetailSchema';

export interface DeliveryItemFormProps extends RequiredTestIdProps {
  control: FormControl<ReceiveNoteItemFormType>;
  deliveryNoteQuantity: number | Nullish;
  deliveryNoteUnitPrice: number | Nullish;
  deliveryNoteTotalPrice: number | Nullish;
  handlingUnit: string | Nullish;
  currency: string;
  isChecked: boolean;
  isEditingDisabled: boolean;
  isFromReceiveNote?: boolean;
  receiveNoteState?: 'PENDING' | 'COMPLETED' | Nullish;
  receiveNoteType?: string | Nullish;
}

export function DeliveryItemForm(props: DeliveryItemFormProps) {
  const deliveryNoteQuantityHelperText = `${i18n.t('entity.warehouse.labels.deliveryNoteQuantity')}: ${props.deliveryNoteQuantity ?? 0} ${props.handlingUnit?.toLowerCase() ?? FALLBACK_HANDLING_UNIT.toLowerCase()}`;
  const deliveryNoteUnitPriceHelperText = `${i18n.t('entity.warehouse.labels.deliveryNoteUnitPrice')}: ${props.deliveryNoteUnitPrice ?? 0} ${props.currency}`;
  const deliveryNoteTotalPriceHelperText = `${i18n.t('entity.warehouse.labels.deliveryNoteTotalPrice')}: ${props.deliveryNoteTotalPrice ?? 0} ${props.currency}`;

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const isReceiveNotePending = props.receiveNoteState === 'PENDING';
  const specialConditionForEnablingVerifiedFields =
    isReceiveNotePending && props.receiveNoteType === 'PURCHASE' && !props.isFromReceiveNote;

  const defaultDisabledCondition =
    not(props.isChecked) || props.isEditingDisabled || isFalse(props.isFromReceiveNote);

  const isVerifiedFieldsDisabled =
    !specialConditionForEnablingVerifiedFields && defaultDisabledCondition;

  const isVatDisabled = not(props.isChecked) || props.isEditingDisabled;

  const getVerifiedFieldsTooltipLabel = () => {
    if (not(props.isChecked)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledItemNotChecked');
    }
    if (props.isEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledReceiveNoteCompleted');
    }
    if (isFalse(props.isFromReceiveNote)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledNotFromReceiveNote');
    }
    return '';
  };

  const getVatTooltipLabel = () => {
    if (not(props.isChecked)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledItemNotChecked');
    }
    if (props.isEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledReceiveNoteCompleted');
    }
    return '';
  };

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.deliveryItem')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <Tooltip label={getVerifiedFieldsTooltipLabel()} isDisabled={!isVerifiedFieldsDisabled}>
            <FormField
              type="number"
              name="deliveryItem.quantityVerified"
              label={i18n.t('general.labels.verifiedQuantity')}
              control={props.control}
              isDisabled={isVerifiedFieldsDisabled}
              decimalPlaces={4}
              suffix={props.handlingUnit ?? FALLBACK_HANDLING_UNIT}
              helperText={deliveryNoteQuantityHelperText}
              data-testid={suffixTestId('inputs.quantityVerified', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getVerifiedFieldsTooltipLabel()} isDisabled={!isVerifiedFieldsDisabled}>
            <FormField
              type="number"
              name="deliveryItem.purchasePriceVerified"
              label={i18n.t('entity.warehouse.labels.verifiedUnitPrice')}
              control={props.control}
              isDisabled={isVerifiedFieldsDisabled}
              suffix={props.currency}
              decimalPlaces={2}
              helperText={deliveryNoteUnitPriceHelperText}
              data-testid={suffixTestId('inputs.unitPrice', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getVerifiedFieldsTooltipLabel()} isDisabled={!isVerifiedFieldsDisabled}>
            <FormField
              type="number"
              name="deliveryItem.stockValueVerified"
              label={i18n.t('general.labels.verifiedTotalPrice')}
              control={props.control}
              isDisabled={isVerifiedFieldsDisabled}
              suffix={props.currency}
              decimalPlaces={2}
              helperText={deliveryNoteTotalPriceHelperText}
              data-testid={suffixTestId('inputs.totalPrice', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getVatTooltipLabel()} isDisabled={!isVatDisabled}>
            <FormField
              type="choice"
              name="deliveryItem.vatType"
              label={i18n.t('general.labels.vat')}
              options={vatOptions}
              control={props.control}
              isNotClearable
              isDisabled={isVatDisabled}
              menuInPortal
              data-testid={suffixTestId('inputs.vatType', props)}
            />
          </Tooltip>
        </GridItem>
      </Grid>
    </VStack>
  );
}
